import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  adminApi,
  type CMSFaq,
  type CMSFaqInput,
  type CMSPolicy,
  type CMSPolicyInput,
  type CMSAppSettings,
  type CMSAppVersion,
  type CMSContact,
} from '@/lib/adminApi';
import { Card, DataTable, Spinner, EmptyState, Badge, type Column, type BadgeTone } from '@/components/ui';
import { IconLogs, IconFlag } from '@/components/ui/icons';
import { AdminPage, AdminPageHeader } from '@/components/admin/shared';
import { Drawer } from '@/components/admin/Drawer';
import { useAdminPermission } from '@/lib/useAdminPermission';

/** Content / CMS — the staff surface over content-svc's CMS tables (faqs,
 *  privacy_policies, the app_settings singleton, the contact_settings singleton).
 *  The rider/driver apps READ these via content-svc's public endpoints; here
 *  staff author them.
 *
 *  READ + AUTHORED: staff with cms.manage can create / edit / delete FAQs and
 *  policies, and edit the app-settings + contact singletons. admin-svc proxies
 *  every WRITE to content-svc (which owns the tables; audited + RBAC-gated
 *  server-side). Read-only roles still see everything. Each section is DEFENSIVE
 *  (shows a "not yet available" state when the table is absent, and a clean 503
 *  when admin-svc can't reach content-svc yet). Covers the Filament FAQ /
 *  policy / app-settings / contact resources. */
export function Content() {
  const [tab, setTab] = useState<TabKey>('faqs');
  const canManage = useAdminPermission('cms.manage');

  return (
    <AdminPage>
      <AdminPageHeader
        title="Content"
        subtitle="FAQs, legal policies, app settings and support contact (owned by content-svc)."
      />

      <Tabs tab={tab} onTab={setTab} />

      <div className="mt-4">
        {tab === 'faqs' ? <FaqsSection canManage={canManage} /> : null}
        {tab === 'policies' ? <PoliciesSection canManage={canManage} /> : null}
        {tab === 'app-settings' ? <AppSettingsSection canManage={canManage} /> : null}
        {tab === 'app-version' ? <AppVersionSection canManage={canManage} /> : null}
        {tab === 'contact' ? <ContactSection canManage={canManage} /> : null}
        {tab === 'complaint-categories' ? <ComplaintCategoriesSection /> : null}
      </div>
    </AdminPage>
  );
}

// ─── tabs ────────────────────────────────────────────────────

type TabKey = 'faqs' | 'policies' | 'app-settings' | 'app-version' | 'contact' | 'complaint-categories';

const TABS: Array<{ key: TabKey; label: string }> = [
  { key: 'faqs', label: 'FAQs' },
  { key: 'policies', label: 'Policies' },
  { key: 'app-settings', label: 'App settings' },
  { key: 'app-version', label: 'App version / Force update' },
  { key: 'contact', label: 'Contact' },
  { key: 'complaint-categories', label: 'Complaint categories' },
];

function Tabs({ tab, onTab }: { tab: TabKey; onTab: (t: TabKey) => void }) {
  return (
    <div className="flex flex-wrap items-center gap-1 border-b border-ink-800">
      {TABS.map((t) => (
        <button
          key={t.key}
          type="button"
          onClick={() => onTab(t.key)}
          className={
            tab === t.key
              ? 'rounded-t-md border-b-2 border-brand-500 px-3 py-2 text-xs font-semibold text-slate-100'
              : 'rounded-t-md border-b-2 border-transparent px-3 py-2 text-xs text-muted hover:text-slate-200'
          }
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

/** The CMS audience filter (user_type_id). content-svc keys faqs + policies by
 *  audience: 1 = customer, 2 = driver. */
const AUDIENCE_OPTIONS: Array<{ value: number; label: string }> = [
  { value: 1, label: 'Customer' },
  { value: 2, label: 'Driver' },
];

function audienceName(id: number): string {
  return id === 2 ? 'Driver' : id === 1 ? 'Customer' : '—';
}

/** status: 1 = published (riders/drivers see it), 0 = draft (hidden). */
function StatusBadge({ status }: { status: number }) {
  const tone: BadgeTone = status === 1 ? 'ok' : 'neutral';
  return (
    <Badge tone={tone} dot>
      {status === 1 ? 'published' : 'draft'}
    </Badge>
  );
}

/** The shared "not yet available" empty state for a CMS table absent from this
 *  Postgres. */
function UnavailableCard({ note }: { note?: string }) {
  return (
    <Card>
      <EmptyState
        title="Not yet in Postgres"
        description={
          note ??
          'This content table is owned by content-svc and is not in this Postgres yet. This section lights up automatically once it lands.'
        }
        icon={<IconLogs className="h-6 w-6" />}
      />
    </Card>
  );
}

// ─── FAQs ────────────────────────────────────────────────────

function FaqsSection({ canManage }: { canManage: boolean }) {
  const [scope, setScope] = useState<'' | 'customer' | 'driver'>('');
  // null = closed; 'new' = create; a CMSFaq = edit that row.
  const [editing, setEditing] = useState<CMSFaq | 'new' | null>(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'cms', 'faqs', scope],
    queryFn: ({ signal }) => adminApi.cmsFaqs(scope || undefined, signal),
  });

  const unavailable = data && data.available === false;

  return (
    <>
      <div className="mb-3 flex items-center justify-between gap-3">
        <ScopeFilter scope={scope} onScope={setScope} />
        {canManage && !unavailable ? <NewButton label="New FAQ" onClick={() => setEditing('new')} /> : null}
      </div>

      {unavailable ? (
        <UnavailableCard note={data?.note} />
      ) : (
        <Card flush>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner label="Loading FAQs…" />
            </div>
          ) : isError ? (
            <EmptyState title="Couldn't load FAQs" description={(error as Error)?.message} />
          ) : (
            <FaqsTable rows={data?.items ?? []} canManage={canManage} onEdit={(f) => setEditing(f)} />
          )}
        </Card>
      )}

      {editing ? <FaqDrawer faq={editing === 'new' ? null : editing} onClose={() => setEditing(null)} /> : null}
    </>
  );
}

function FaqsTable({
  rows,
  canManage,
  onEdit,
}: {
  rows: CMSFaq[];
  canManage: boolean;
  onEdit: (f: CMSFaq) => void;
}) {
  const columns: Array<Column<CMSFaq>> = [
    {
      key: 'question',
      header: 'Question',
      cell: (r) => <span className="font-medium text-slate-100">{r.question}</span>,
    },
    {
      key: 'answer',
      header: 'Answer',
      cell: (r) => <span className="line-clamp-2 text-xs text-slate-300">{r.answer}</span>,
    },
    {
      key: 'audience',
      header: 'Audience',
      cell: (r) => <Badge tone={r.user_type_id === 2 ? 'info' : 'neutral'}>{audienceName(r.user_type_id)}</Badge>,
    },
    { key: 'status', header: 'Status', cell: (r) => <StatusBadge status={r.status} /> },
  ];
  if (canManage) {
    columns.push(editColumn(onEdit));
  }
  return (
    <DataTable<CMSFaq>
      columns={columns}
      rows={rows}
      rowKey={(r) => String(r.id)}
      stickyHeader
      empty={<EmptyState title="No FAQs" description="No FAQs yet." icon={<IconLogs className="h-6 w-6" />} />}
    />
  );
}

interface FaqFormState {
  user_type_id: number;
  question: string;
  answer: string;
  status: boolean; // true = published (1)
}

function FaqDrawer({ faq, onClose }: { faq: CMSFaq | null; onClose: () => void }) {
  const isEdit = faq !== null;
  const qc = useQueryClient();
  const [form, setForm] = useState<FaqFormState>(() => seedFaq(faq));
  const [clientError, setClientError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (input: CMSFaqInput) => (isEdit ? adminApi.updateFaq(faq!.id, input) : adminApi.createFaq(input)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'cms', 'faqs'] });
      onClose();
    },
  });

  const set = <K extends keyof FaqFormState>(key: K, value: FaqFormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const onSubmit = () => {
    setClientError(null);
    if (!form.question.trim()) return setClientError('Question is required.');
    if (!form.answer.trim()) return setClientError('Answer is required.');
    mutation.mutate({
      user_type_id: form.user_type_id,
      question: form.question.trim(),
      answer: form.answer.trim(),
      status: form.status ? 1 : 0,
    });
  };

  return (
    <Drawer
      title={isEdit ? `Edit FAQ #${faq!.id}` : 'New FAQ'}
      subtitle={isEdit ? audienceName(faq!.user_type_id) : 'Author a help-centre FAQ'}
      onClose={onClose}
    >
      <div className="space-y-4">
        <Card>
          <div className="space-y-3">
            <SelectField
              label="Audience"
              value={String(form.user_type_id)}
              onChange={(v) => set('user_type_id', Number(v))}
              options={AUDIENCE_OPTIONS.map((o) => ({ value: String(o.value), label: o.label }))}
            />
            <TextField label="Question" value={form.question} onChange={(v) => set('question', v)} placeholder="How do I top up my wallet?" />
            <TextAreaField label="Answer" value={form.answer} onChange={(v) => set('answer', v)} rows={3} />
            <PublishToggle checked={form.status} onChange={(v) => set('status', v)} />
          </div>
        </Card>

        <DrawerActions
          isEdit={isEdit}
          pending={mutation.isPending}
          onSubmit={onSubmit}
          onClose={onClose}
          createLabel="Create FAQ"
          clientError={clientError}
          serverError={mutation.isError ? (mutation.error as Error)?.message : null}
        />
        {isEdit && canDelete(faq) ? (
          <DeleteRow
            label="Delete this FAQ"
            onDelete={() => adminApi.deleteFaq(faq!.id)}
            invalidateKey={['admin', 'cms', 'faqs']}
            onDone={onClose}
          />
        ) : null}
      </div>
    </Drawer>
  );
}

function seedFaq(f: CMSFaq | null): FaqFormState {
  if (!f) return { user_type_id: 1, question: '', answer: '', status: true };
  return {
    user_type_id: f.user_type_id || 1,
    question: f.question,
    answer: f.answer,
    status: f.status === 1,
  };
}

// ─── Policies ────────────────────────────────────────────────

function PoliciesSection({ canManage }: { canManage: boolean }) {
  const [scope, setScope] = useState<'' | 'customer' | 'driver'>('');
  const [editing, setEditing] = useState<CMSPolicy | 'new' | null>(null);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'cms', 'policies', scope],
    queryFn: ({ signal }) => adminApi.cmsPolicies(scope || undefined, signal),
  });

  const unavailable = data && data.available === false;

  return (
    <>
      <div className="mb-3 flex items-center justify-between gap-3">
        <ScopeFilter scope={scope} onScope={setScope} />
        {canManage && !unavailable ? <NewButton label="New policy" onClick={() => setEditing('new')} /> : null}
      </div>

      {unavailable ? (
        <UnavailableCard note={data?.note} />
      ) : (
        <Card flush>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner label="Loading policies…" />
            </div>
          ) : isError ? (
            <EmptyState title="Couldn't load policies" description={(error as Error)?.message} />
          ) : (
            <PoliciesTable rows={data?.items ?? []} canManage={canManage} onEdit={(p) => setEditing(p)} />
          )}
        </Card>
      )}

      {editing ? <PolicyDrawer policy={editing === 'new' ? null : editing} onClose={() => setEditing(null)} /> : null}
    </>
  );
}

function PoliciesTable({
  rows,
  canManage,
  onEdit,
}: {
  rows: CMSPolicy[];
  canManage: boolean;
  onEdit: (p: CMSPolicy) => void;
}) {
  const columns: Array<Column<CMSPolicy>> = [
    { key: 'title', header: 'Title', cell: (r) => <span className="font-medium text-slate-100">{r.title}</span> },
    { key: 'slug', header: 'Slug', cell: (r) => <span className="metric text-xs text-slate-300">{r.slug || '—'}</span> },
    {
      key: 'audience',
      header: 'Audience',
      cell: (r) => <Badge tone={r.user_type_id === 2 ? 'info' : 'neutral'}>{audienceName(r.user_type_id)}</Badge>,
    },
    { key: 'status', header: 'Status', cell: (r) => <StatusBadge status={r.status} /> },
  ];
  if (canManage) {
    columns.push(editColumn(onEdit));
  }
  return (
    <DataTable<CMSPolicy>
      columns={columns}
      rows={rows}
      rowKey={(r) => String(r.id)}
      stickyHeader
      empty={<EmptyState title="No policies" description="No legal documents yet." icon={<IconLogs className="h-6 w-6" />} />}
    />
  );
}

interface PolicyFormState {
  slug: string;
  title: string;
  description: string;
  user_type_id: number;
  status: boolean;
}

function PolicyDrawer({ policy, onClose }: { policy: CMSPolicy | null; onClose: () => void }) {
  const isEdit = policy !== null;
  const qc = useQueryClient();
  const [form, setForm] = useState<PolicyFormState>(() => seedPolicy(policy));
  const [clientError, setClientError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (input: CMSPolicyInput) =>
      isEdit ? adminApi.updatePolicy(policy!.id, input) : adminApi.createPolicy(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'cms', 'policies'] });
      onClose();
    },
  });

  const set = <K extends keyof PolicyFormState>(key: K, value: PolicyFormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const onSubmit = () => {
    setClientError(null);
    if (!form.title.trim()) return setClientError('Title is required.');
    if (!form.description.trim()) return setClientError('Description is required.');
    mutation.mutate({
      slug: form.slug.trim(),
      title: form.title.trim(),
      description: form.description,
      user_type_id: form.user_type_id,
      status: form.status ? 1 : 0,
    });
  };

  return (
    <Drawer
      title={isEdit ? `Edit policy #${policy!.id}` : 'New policy'}
      subtitle={isEdit ? policy!.title : 'Author a legal document (privacy / terms / about)'}
      onClose={onClose}
    >
      <div className="space-y-4">
        <Card>
          <div className="space-y-3">
            <SelectField
              label="Audience"
              value={String(form.user_type_id)}
              onChange={(v) => set('user_type_id', Number(v))}
              options={AUDIENCE_OPTIONS.map((o) => ({ value: String(o.value), label: o.label }))}
            />
            <TextField
              label="Slug"
              hint="The stable key the app fetches by (e.g. privacy-policy, terms, about-us)."
              value={form.slug}
              onChange={(v) => set('slug', v)}
              placeholder="privacy-policy"
            />
            <TextField label="Title" value={form.title} onChange={(v) => set('title', v)} placeholder="Privacy Policy" />
            <TextAreaField
              label="Body"
              hint="Plain text or HTML, as the app renders it."
              value={form.description}
              onChange={(v) => set('description', v)}
              rows={8}
            />
            <PublishToggle checked={form.status} onChange={(v) => set('status', v)} />
          </div>
        </Card>

        <DrawerActions
          isEdit={isEdit}
          pending={mutation.isPending}
          onSubmit={onSubmit}
          onClose={onClose}
          createLabel="Create policy"
          clientError={clientError}
          serverError={mutation.isError ? (mutation.error as Error)?.message : null}
        />
        {isEdit && canDelete(policy) ? (
          <DeleteRow
            label="Delete this policy"
            onDelete={() => adminApi.deletePolicy(policy!.id)}
            invalidateKey={['admin', 'cms', 'policies']}
            onDone={onClose}
          />
        ) : null}
      </div>
    </Drawer>
  );
}

function seedPolicy(p: CMSPolicy | null): PolicyFormState {
  if (!p)
    return { slug: '', title: '', description: '', user_type_id: 1, status: true };
  return {
    slug: p.slug ?? '',
    title: p.title,
    description: p.description,
    user_type_id: p.user_type_id || 1,
    status: p.status === 1,
  };
}

// ─── App settings (singleton) ────────────────────────────────

function AppSettingsSection({ canManage }: { canManage: boolean }) {
  const qc = useQueryClient();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'cms', 'app-settings'],
    queryFn: ({ signal }) => adminApi.cmsAppSettings(signal),
  });
  const [form, setForm] = useState<CMSAppSettings | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const mutation = useMutation({
    mutationFn: (input: CMSAppSettings) => adminApi.updateAppSettings(input),
    onSuccess: () => {
      setSavedAt(Date.now());
      qc.invalidateQueries({ queryKey: ['admin', 'cms', 'app-settings'] });
    },
  });

  if (isLoading) {
    return (
      <Card>
        <div className="flex items-center justify-center py-12">
          <Spinner label="Loading app settings…" />
        </div>
      </Card>
    );
  }
  if (isError) return <Card><EmptyState title="Couldn't load app settings" description={(error as Error)?.message} /></Card>;
  if (data && data.available === false) return <UnavailableCard note={data.note} />;

  // The form is seeded once from the loaded row (or empty defaults when the
  // table exists but no row is seeded yet — configured:false).
  const current = form ?? data?.app_settings ?? EMPTY_APP_SETTINGS;
  const set = <K extends keyof CMSAppSettings>(key: K, value: CMSAppSettings[K]) =>
    setForm((f) => ({ ...(f ?? data?.app_settings ?? EMPTY_APP_SETTINGS), [key]: value }));

  const onSubmit = () => mutation.mutate(current);

  return (
    <div className="space-y-4">
      {data && data.configured === false ? (
        <Card>
          <p className="text-xs text-warn">
            No app_settings row is seeded yet. Saving will create it.
          </p>
        </Card>
      ) : null}

      <Card>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <TextField label="App name" value={current.app_name} onChange={(v) => set('app_name', v)} disabled={!canManage} />
          <TextField label="Logo URL" value={current.logo} onChange={(v) => set('logo', v)} disabled={!canManage} />
          <TextField label="Customer app version" value={current.app_version} onChange={(v) => set('app_version', v)} disabled={!canManage} />
          <TextField label="Driver app version" value={current.driver_app_version} onChange={(v) => set('driver_app_version', v)} disabled={!canManage} />
          <TextField label="Currency" value={current.default_currency} onChange={(v) => set('default_currency', v)} disabled={!canManage} placeholder="AUD" />
          <TextField label="Currency symbol" value={current.default_currency_symbol} onChange={(v) => set('default_currency_symbol', v)} disabled={!canManage} placeholder="$" />
          <TextField label="Currency short code" value={current.currency_short_code} onChange={(v) => set('currency_short_code', v)} disabled={!canManage} />
          <TextField label="Default country" value={current.default_country} onChange={(v) => set('default_country', v)} disabled={!canManage} placeholder="AU" />
          <TextField label="Phone code" value={current.phone_code} onChange={(v) => set('phone_code', v)} disabled={!canManage} placeholder="+61" />
          <TextField label="Default language" value={current.default_language} onChange={(v) => set('default_language', v)} disabled={!canManage} placeholder="en" />
          <NumberField label="Driver trip time (min)" value={current.driver_trip_time} onChange={(v) => set('driver_trip_time', v)} disabled={!canManage} />
          <NumberField label="Capital latitude" value={current.capital_lat} onChange={(v) => set('capital_lat', v)} disabled={!canManage} />
          <NumberField label="Capital longitude" value={current.capital_lng} onChange={(v) => set('capital_lng', v)} disabled={!canManage} />
        </div>

        <div className="mt-4">
          <TextAreaField label="About us" value={current.about_us} onChange={(v) => set('about_us', v)} rows={3} disabled={!canManage} />
        </div>
      </Card>

      {canManage ? (
        <SaveBar
          pending={mutation.isPending}
          onSave={onSubmit}
          savedAt={savedAt}
          serverError={mutation.isError ? (mutation.error as Error)?.message : null}
        />
      ) : (
        <ReadOnlyNote />
      )}
    </div>
  );
}

const EMPTY_APP_SETTINGS: CMSAppSettings = {
  id: 0,
  app_name: '',
  logo: '',
  app_version: '',
  driver_app_version: '',
  default_currency: '',
  default_currency_symbol: '',
  currency_short_code: '',
  default_country: '',
  phone_code: '',
  about_us: '',
  subscription_status: 0,
  language_status: 0,
  default_language: '',
  polyline_status: 0,
  driver_trip_time: 0,
  capital_lat: 0,
  capital_lng: 0,
};

// ─── App version / Force update ──────────────────────────────

/** AppVersionSection — publishes the latest BUILD per platform per app and the
 *  force-update kill switch. The apps compare their installed build number
 *  (version_code) against the published one; force_update=1 HARD-BLOCKS any
 *  older build until the user updates. FOUR independent sub-forms — Customer
 *  (Android + iOS) and Driver (Android + iOS) — each saving on its own via
 *  PUT /v1/admin/cms/app-version (carrying platform + user_type_id).
 *
 *  Seeded from adminApi.cmsAppVersions() (the public content-svc app-settings
 *  payload). Seeding is best-effort — if it isn't reachable the forms simply
 *  start empty and a save still publishes a build. */
function AppVersionSection({ canManage }: { canManage: boolean }) {
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'cms', 'app-versions'],
    queryFn: ({ signal }) => adminApi.cmsAppVersions(signal),
  });

  if (isLoading) {
    return (
      <Card>
        <div className="flex items-center justify-center py-12">
          <Spinner label="Loading app versions…" />
        </div>
      </Card>
    );
  }

  // key on the seed identity so each form RE-SEEDS when the published values
  // arrive/change (the inner useState only reads `seed` on first mount).
  const seedKey = (v: CMSAppVersion | null) => `${v?.version_code ?? 'none'}-${v?.force_update ?? 0}`;

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">Customer app</div>
        <PlatformVersionForm
          key={`c-android-${seedKey(data?.android ?? null)}`}
          platform={1}
          userTypeId={1}
          appLabel="Customer"
          platformLabel="Android"
          seed={data?.android ?? null}
          canManage={canManage}
        />
        <PlatformVersionForm
          key={`c-ios-${seedKey(data?.ios ?? null)}`}
          platform={2}
          userTypeId={1}
          appLabel="Customer"
          platformLabel="iOS"
          seed={data?.ios ?? null}
          canManage={canManage}
        />
      </div>
      <div className="space-y-4">
        <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">Driver app</div>
        <PlatformVersionForm
          key={`d-android-${seedKey(data?.android_driver ?? null)}`}
          platform={1}
          userTypeId={2}
          appLabel="Driver"
          platformLabel="Android"
          seed={data?.android_driver ?? null}
          canManage={canManage}
        />
        <PlatformVersionForm
          key={`d-ios-${seedKey(data?.ios_driver ?? null)}`}
          platform={2}
          userTypeId={2}
          appLabel="Driver"
          platformLabel="iOS"
          seed={data?.ios_driver ?? null}
          canManage={canManage}
        />
      </div>
    </div>
  );
}

/** One slot's force-update editor — platform (Android = 1, iOS = 2) × app
 *  (userTypeId: customer = 1, driver = 2). version_code is the build number the
 *  apps key the comparison off; version_number is the marketing label;
 *  force_update is the hard-block kill switch. */
function PlatformVersionForm({
  platform,
  userTypeId,
  appLabel,
  platformLabel,
  seed,
  canManage,
}: {
  platform: number;
  userTypeId: number;
  appLabel: string;
  platformLabel: string;
  seed: CMSAppVersion | null;
  canManage: boolean;
}) {
  const qc = useQueryClient();
  const [versionCode, setVersionCode] = useState(seed?.version_code ?? '');
  const [versionNumber, setVersionNumber] = useState(seed?.version_number ?? '');
  const [forced, setForced] = useState((seed?.force_update ?? 0) === 1);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      adminApi.updateAppVersion({
        platform,
        user_type_id: userTypeId,
        version_number: versionNumber.trim(),
        version_code: versionCode.trim(),
        force_update: forced ? 1 : 0,
      }),
    onSuccess: () => {
      setSavedAt(Date.now());
      qc.invalidateQueries({ queryKey: ['admin', 'cms', 'app-versions'] });
    },
  });

  const canSave = canManage && versionCode.trim().length > 0;

  return (
    <Card>
      <div className="mb-3 text-sm font-semibold text-slate-100">
        {appLabel} · {platformLabel}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <TextField
          label="Build number (version_code)"
          hint="The number the apps compare against — bump this on every release."
          value={versionCode}
          onChange={setVersionCode}
          placeholder="e.g. 142"
          disabled={!canManage}
        />
        <TextField
          label="Version label (version_number)"
          hint="The marketing version shown to users."
          value={versionNumber}
          onChange={setVersionNumber}
          placeholder="e.g. 1.4.0"
          disabled={!canManage}
        />
      </div>

      <div className="mt-4 rounded-md border border-warn/30 bg-warn/10 p-3">
        <label className="flex items-center gap-2 text-sm text-slate-200">
          <input
            type="checkbox"
            checked={forced}
            disabled={!canManage}
            onChange={(e) => setForced(e.target.checked)}
            className="h-4 w-4 rounded border-ink-700 bg-ink-850 text-brand-500 focus:ring-brand-500 disabled:opacity-60"
          />
          Force update (hard-block older builds)
        </label>
        <p className="mt-1.5 text-[11px] text-warn">
          Turning this ON blocks EVERY {appLabel} {platformLabel} app whose build number is below this
          value until they update — set the build number carefully.
        </p>
      </div>

      {canManage ? (
        <div className="mt-4">
          <div className="flex items-center gap-3">
            <button
              type="button"
              disabled={!canSave || mutation.isPending}
              onClick={() => mutation.mutate()}
              className="btn-primary"
            >
              {mutation.isPending ? 'Saving…' : `Save ${appLabel} ${platformLabel}`}
            </button>
            {savedAt && !mutation.isPending && !mutation.isError ? (
              <span className="text-xs text-ok">Saved.</span>
            ) : null}
            {mutation.isError ? (
              <span className="text-xs text-danger">{(mutation.error as Error)?.message}</span>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="mt-4">
          <ReadOnlyNote />
        </div>
      )}
    </Card>
  );
}

// ─── Contact (singleton) ─────────────────────────────────────

function ContactSection({ canManage }: { canManage: boolean }) {
  const qc = useQueryClient();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'cms', 'contact'],
    queryFn: ({ signal }) => adminApi.cmsContact(signal),
  });
  const [form, setForm] = useState<CMSContact | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const mutation = useMutation({
    mutationFn: (input: CMSContact) => adminApi.updateContact(input),
    onSuccess: () => {
      setSavedAt(Date.now());
      qc.invalidateQueries({ queryKey: ['admin', 'cms', 'contact'] });
    },
  });

  if (isLoading) {
    return (
      <Card>
        <div className="flex items-center justify-center py-12">
          <Spinner label="Loading contact…" />
        </div>
      </Card>
    );
  }
  if (isError) return <Card><EmptyState title="Couldn't load contact" description={(error as Error)?.message} /></Card>;
  if (data && data.available === false) return <UnavailableCard note={data.note} />;

  const current = form ?? data?.contact ?? EMPTY_CONTACT;
  const set = <K extends keyof CMSContact>(key: K, value: CMSContact[K]) =>
    setForm((f) => ({ ...(f ?? data?.contact ?? EMPTY_CONTACT), [key]: value }));

  const onSubmit = () => mutation.mutate(current);

  return (
    <div className="space-y-4">
      {data && data.configured === false ? (
        <Card>
          <p className="text-xs text-warn">No contact_settings row is seeded yet. Saving will create it.</p>
        </Card>
      ) : null}

      <Card>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <TextField label="Phone number" value={current.phone_number} onChange={(v) => set('phone_number', v)} disabled={!canManage} placeholder="+61 ..." />
          <TextField label="Email" value={current.email} onChange={(v) => set('email', v)} disabled={!canManage} placeholder="support@telride.com.au" />
          <TextField label="Latitude" value={current.lat} onChange={(v) => set('lat', v)} disabled={!canManage} />
          <TextField label="Longitude" value={current.lng} onChange={(v) => set('lng', v)} disabled={!canManage} />
        </div>
        <div className="mt-3">
          <TextAreaField label="Address" value={current.address} onChange={(v) => set('address', v)} rows={2} disabled={!canManage} />
        </div>
      </Card>

      {canManage ? (
        <SaveBar
          pending={mutation.isPending}
          onSave={onSubmit}
          savedAt={savedAt}
          serverError={mutation.isError ? (mutation.error as Error)?.message : null}
        />
      ) : (
        <ReadOnlyNote />
      )}
    </div>
  );
}

const EMPTY_CONTACT: CMSContact = { id: 0, phone_number: '', email: '', address: '', lat: '', lng: '' };

// ─── Complaint categories (read-only) ────────────────────────

/** Complaint categories drive the rider/driver complaint form. content-svc owns
 *  the `complaint_categories` / `complaint_sub_categories` tree and seeds it; it
 *  exposes no admin authoring endpoint, so this section is informational —
 *  changes are made via content-svc's seed/migration, not the portal. */
function ComplaintCategoriesSection() {
  return (
    <Card>
      <EmptyState
        title="Managed by content-svc"
        description="Complaint categories and sub-categories are seeded and owned by content-svc. They power the rider/driver complaint form. There is no portal authoring endpoint for them yet — update them via content-svc's seed/migration."
        icon={<IconFlag className="h-6 w-6" />}
      />
    </Card>
  );
}

// ─── shared field + control components ───────────────────────

function ScopeFilter({
  scope,
  onScope,
}: {
  scope: '' | 'customer' | 'driver';
  onScope: (s: '' | 'customer' | 'driver') => void;
}) {
  const opts: Array<{ value: '' | 'customer' | 'driver'; label: string }> = [
    { value: '', label: 'All audiences' },
    { value: 'customer', label: 'Customer' },
    { value: 'driver', label: 'Driver' },
  ];
  return (
    <select
      value={scope}
      onChange={(e) => onScope(e.target.value as '' | 'customer' | 'driver')}
      className="rounded-lg border border-ink-700 bg-ink-850 px-3 py-1.5 text-sm text-slate-100 focus:border-brand-500 focus:outline-none"
    >
      {opts.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

function NewButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="btn-primary"
    >
      {label}
    </button>
  );
}

/** The Edit action column shared by the FAQ + policy tables. */
function editColumn<T>(onEdit: (row: T) => void): Column<T> {
  return {
    key: 'actions',
    header: '',
    align: 'right',
    cell: (r) => (
      <button
        type="button"
        onClick={() => onEdit(r)}
        className="rounded-md border border-ink-700 px-2 py-1 text-xs text-brand-400 hover:bg-ink-800"
      >
        Edit
      </button>
    ),
  };
}

function TextField({
  label,
  hint,
  value,
  onChange,
  placeholder,
  disabled,
  dir,
}: {
  label: string;
  hint?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  disabled?: boolean;
  dir?: 'rtl' | 'ltr';
}) {
  return (
    <div>
      <label className="block text-[11px] text-muted">{label}</label>
      <input
        type="text"
        dir={dir}
        disabled={disabled}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 input disabled:opacity-60"
      />
      {hint ? <p className="mt-0.5 text-[11px] text-muted">{hint}</p> : null}
    </div>
  );
}

function NumberField({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  disabled?: boolean;
}) {
  return (
    <div>
      <label className="block text-[11px] text-muted">{label}</label>
      <input
        type="number"
        disabled={disabled}
        value={Number.isFinite(value) ? value : 0}
        onChange={(e) => onChange(Number(e.target.value))}
        className="mt-1 input disabled:opacity-60"
      />
    </div>
  );
}

function TextAreaField({
  label,
  hint,
  value,
  onChange,
  rows = 3,
  disabled,
  dir,
}: {
  label: string;
  hint?: string;
  value: string;
  onChange: (v: string) => void;
  rows?: number;
  disabled?: boolean;
  dir?: 'rtl' | 'ltr';
}) {
  return (
    <div>
      <label className="block text-[11px] text-muted">{label}</label>
      <textarea
        dir={dir}
        disabled={disabled}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        className="mt-1 input disabled:opacity-60"
      />
      {hint ? <p className="mt-0.5 text-[11px] text-muted">{hint}</p> : null}
    </div>
  );
}

function SelectField({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  return (
    <div>
      <label className="block text-[11px] text-muted">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 input"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function PublishToggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center gap-2 text-sm text-slate-200">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 rounded border-ink-700 bg-ink-850 text-brand-500 focus:ring-brand-500"
      />
      Published (visible in the app)
    </label>
  );
}

/** The Save / Cancel + error row shared by the create/edit drawers. */
function DrawerActions({
  isEdit,
  pending,
  onSubmit,
  onClose,
  createLabel,
  clientError,
  serverError,
}: {
  isEdit: boolean;
  pending: boolean;
  onSubmit: () => void;
  onClose: () => void;
  createLabel: string;
  clientError: string | null;
  serverError: string | null;
}) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <button
        type="button"
        disabled={pending}
        onClick={onSubmit}
        className="btn-primary"
      >
        {pending ? 'Saving…' : isEdit ? 'Save changes' : createLabel}
      </button>
      <button
        type="button"
        disabled={pending}
        onClick={onClose}
        className="btn-secondary"
      >
        Cancel
      </button>
      {clientError ? <span className="text-xs text-danger">{clientError}</span> : null}
      {serverError ? <span className="text-xs text-danger">{serverError}</span> : null}
    </div>
  );
}

/** The Save bar for the singleton editors (app-settings, contact). */
function SaveBar({
  pending,
  onSave,
  savedAt,
  serverError,
}: {
  pending: boolean;
  onSave: () => void;
  savedAt: number | null;
  serverError: string | null;
}) {
  return (
    <div className="flex items-center gap-3">
      <button
        type="button"
        disabled={pending}
        onClick={onSave}
        className="btn-primary"
      >
        {pending ? 'Saving…' : 'Save changes'}
      </button>
      {savedAt && !pending && !serverError ? <span className="text-xs text-ok">Saved.</span> : null}
      {serverError ? <span className="text-xs text-danger">{serverError}</span> : null}
    </div>
  );
}

function ReadOnlyNote() {
  return <p className="text-xs text-muted">You have read-only access. The cms.manage permission is required to edit.</p>;
}

/** A destructive delete row inside an edit drawer, with an inline confirm step.
 *  onDelete performs the API call; onDone closes the drawer on success. */
function DeleteRow({
  label,
  onDelete,
  invalidateKey,
  onDone,
}: {
  label: string;
  onDelete: () => Promise<unknown>;
  invalidateKey: unknown[];
  onDone: () => void;
}) {
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const mutation = useMutation({
    mutationFn: () => onDelete(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: invalidateKey });
      onDone();
    },
  });

  return (
    <div className="border-t border-ink-800 pt-3">
      {confirming ? (
        <div className="flex items-center gap-3">
          <span className="text-xs text-danger">Delete permanently?</span>
          <button
            type="button"
            disabled={mutation.isPending}
            onClick={() => mutation.mutate()}
            className="rounded-md bg-danger px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-50"
          >
            {mutation.isPending ? 'Deleting…' : 'Confirm delete'}
          </button>
          <button
            type="button"
            disabled={mutation.isPending}
            onClick={() => setConfirming(false)}
            className="btn-secondary"
          >
            Keep
          </button>
          {mutation.isError ? <span className="text-xs text-danger">{(mutation.error as Error)?.message}</span> : null}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setConfirming(true)}
          className="rounded-md border border-danger/40 px-3 py-1.5 text-xs text-danger hover:bg-danger/10"
        >
          {label}
        </button>
      )}
    </div>
  );
}

/** Delete affordance is gated on cms.manage. The page only renders the edit
 *  drawer (which hosts the delete row) for managers, so this is true whenever a
 *  row is being edited — kept as an explicit guard for clarity. */
function canDelete(row: CMSFaq | CMSPolicy | null): boolean {
  return row !== null;
}
